import {
  ClaimResponse,
  useMedicalClaimSearch,
} from '@sentara-health/sentara-api-hooks-core';
import {
  generateHashedKey,
  getDecryptedValue,
  safeJsonParse,
  setEncryptedValue,
  errorhandler,
  useRelatedClaim,
  NavigateConst,
  formatMedicalText,
  useMedicalClaimItem,
  apiResponseSafeCheck,
  SortField,
  useDeviceType,
  DeviceTypes,
  useClaimsSortFilter,
  domainForSegment,
  useAnalytics,
} from 'common';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Loader,
  Paragraph,
  Button,
  ChatMinimizeIcon,
  Headings,
  Badge,
  Link,
  ContactInfo,
  FilterIconWhite,
  FilterIconWhiteWithBg,
  Select,
} from '@sentara-health/optimahealth_web';
import { ClaimsAccordionWrapper } from './ClaimsAccordianWrapper';
import { ClaimsFilter } from './ClaimsFilter';
import {
  ClaimsFilterState,
  initialClaimsFilterState,
} from '../../common/types/claimsFilters';
import { logoutErrorHandler } from '../../common/utils/LogoutErrorHandler';
import { GlobalContext, isAvMed } from 'context';

const SuperRelatedClaims = () => {
  const [claimData, setClaimData] = useState<ClaimResponse | null>(null);
  const [allClaims, setAllClaims] = useState<ClaimResponse[]>([]);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { medicalClaimSearch } = useMedicalClaimSearch();
  const { serviceNumber } = useContext(GlobalContext);
  const { claimId, memberId, mId, memberName, showMedical, relatedClaimsTracked } = location.state ?? {};
  const { trackContentListing } = useAnalytics();
  const hasTrackedRelatedClaimsView = useRef(false);

  const trackClaimsAdobeEvent = (
    contentId: string,
    contentTitle: string,
    itemPosition: number,
    additionalFields: Record<string, string | number> = {}
  ) => {
    trackContentListing(
      contentId,
      contentTitle,
      itemPosition,
      undefined,
      { extraFields: additionalFields }
    );
  };

  const trackClaimsLoadError = (_errorCode?: string | number) => {
    const isBehavioralClaim = showMedical === false;
    const contentTitle = isBehavioralClaim ? 'Behavioral_claim_details_load_error_view' : 'Medical_claim_details_load_error_view';
    trackContentListing(
      isBehavioralClaim ? '1226' : '1224',
      contentTitle,
      isBehavioralClaim ? 18 : 16,
      `${domainForSegment}${isBehavioralClaim ? '/home/behavioral-claims' : '/home/medical-claims'}`
    );
  };

  const claimsSortOptions = [
    { label: t('medicalClaims.serviceType'), value: 'serviceType' },
    { label: t('common.date'), value: 'dateOfService' },
    { label: t('common.status'), value: 'status' },
    { label: t('medicalClaims.location'), value: 'location' },
    { label: t('common.amount'), value: 'amountOwed' },
    { label: t('medicalClaims.providerName'), value: 'providerName' },
  ];

  const [loading, setLoading] = useState(false);
  // Sorting & Filtering state (mirrors SuperMedicalClaims)
  const [selectedSortField, setSelectedSortField] = useState<SortField | ''>(
    ''
  );
  const [showFilter, setShowFilter] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<ClaimsFilterState>(
    initialClaimsFilterState
  );
  const searchFnRef = useRef(medicalClaimSearch);
  const requestCounterRef = useRef(0);
  const [phone, setPhone] = useState<{
    data?: { benefitPhone1800?: string; benefitPhoneLocal?: string };
  }>({});

  useEffect(() => {
    searchFnRef.current = medicalClaimSearch;
  }, [medicalClaimSearch]);

  const findClaimById = (
    claims: ClaimResponse[],
    id: string
  ): ClaimResponse | null => {
    if (!Array.isArray(claims)) return null;
    return (
      claims.find((claim) =>
        claim.identifier?.some(
          (ident) => ident?.system?.includes('claimId') && ident.value === id
        )
      ) ?? null
    );
  };

  const handleCachedData = async (memberCacheKey: string, claimId: string) => {
    const cachedValue = await getDecryptedValue(memberCacheKey);
    if (!cachedValue) return null;

    const parsed = safeJsonParse<ClaimResponse[]>(cachedValue, []);
    if (parsed.length === 0) return null;

    const cachedClaim = findClaimById(parsed, claimId);
    if (!cachedClaim) return null;

    setClaimData(cachedClaim);
    setAllClaims(parsed);
    setLoading(false);
    return true;
  };

  const processApiResponse = (apiResponse: any) => {
    if (apiResponse?.error || apiResponse?.errorCode) {
      if (logoutErrorHandler(apiResponse.errorCode ?? apiResponse.error))
        return null;
      trackClaimsLoadError(apiResponse?.errorCode);
      setAllClaims([]);
      setClaimData(null);
      return null;
    }

    let allMemberClaims: ClaimResponse[] = [];
    if (Array.isArray(apiResponse?.data)) {
      allMemberClaims = apiResponse.data;
    } else if (apiResponse?.data) {
      allMemberClaims = [apiResponse.data];
    }

    return allMemberClaims;
  };

  const handleClaimData = async (
    allMemberClaims: ClaimResponse[],
    claimId: string,
    memberCacheKey: string
  ) => {
    const specificClaim = findClaimById(allMemberClaims, claimId);

    if (specificClaim && allMemberClaims.length > 0) {
      await setEncryptedValue(memberCacheKey, JSON.stringify(allMemberClaims));
      setClaimData(specificClaim);
      setAllClaims(allMemberClaims);
    } else {
      setClaimData(null);
      setAllClaims([]);
    }
  };

  const fetchClaimData = useCallback(async () => {
    if (!memberId || !claimId) return;

    const myRequestId = ++requestCounterRef.current;
    setLoading(true);

    try {
      const memberCacheKey = await generateHashedKey(
        `${showMedical ? 'Medical' : 'Behavioral'}_Claims`,
        memberId
      );

      const cacheUsed = await handleCachedData(memberCacheKey, claimId);
      if (cacheUsed) return;

      const requestBody = {
        identifier: [{ system: 'MemberId', value: memberId }],
        claimType: showMedical === false ? 1 : 0,
      };

      const apiResponse = await searchFnRef.current(requestBody);

      if (requestCounterRef.current !== myRequestId) return;

      const allMemberClaims = processApiResponse(apiResponse);
      if (!allMemberClaims) return;

      await handleClaimData(allMemberClaims, claimId, memberCacheKey);
    } catch (err) {
      if (requestCounterRef.current !== myRequestId) return;
      if (!logoutErrorHandler(err)) {
        trackClaimsLoadError();
        errorhandler(err);
      }
      setAllClaims([]);
      setClaimData(null);
    } finally {
      if (requestCounterRef.current === myRequestId) {
        setLoading(false);
      }
    }
  }, [memberId, claimId]);

  useEffect(() => {
    if (!claimData && memberId && claimId) {
      fetchClaimData();
    }

    setPhone(
      typeof serviceNumber === 'string'
        ? safeJsonParse(serviceNumber, {})
        : { data: serviceNumber }
    );
  }, [fetchClaimData, memberId, claimId, claimData, serviceNumber]);

  useEffect(() => {
    if (hasTrackedRelatedClaimsView.current || relatedClaimsTracked) return;
    hasTrackedRelatedClaimsView.current = true;
    trackClaimsAdobeEvent('1211', `${showMedical === false ? 'Behavioral' : 'Medical'}_Claims_related_claims_click`, 5);
  }, [relatedClaimsTracked]);

  const claimDetails = useMedicalClaimItem(
    claimData ?? ({} as ClaimResponse),
    0
  );

  const relatedClaims = useRelatedClaim(claimData, allClaims, claimId);
  const hookResult =
    useClaimsSortFilter({
      claims: relatedClaims,
      selectedSortField,
      appliedFilters,
      pageSize: 10,
    }) || ({} as any);

  const {
    queriedClaims = [],
    displayedClaims = [],
    intersectionObserverTargetRef = { current: null },
    hasMoreItemsToLoad = false,
  } = hookResult;

  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const showDesktopFilterIcon =
    !isMobile && typeof FilterIconWhite === 'function';
  const showMobileFilterIcon =
    isMobile && typeof FilterIconWhiteWithBg === 'function';

  const handleApplyFilter = (filters: ClaimsFilterState) => {
    setAppliedFilters(filters);
    setShowFilter(false);
  };

  const handleClearAll = () => {
    setAppliedFilters(initialClaimsFilterState);
  };

  const handleClick = (action: 'view' | 'pay', id: string) => {
    if (action === 'view') {
      navigate(NavigateConst.claimsSummary, {
        state: { claimId: id, memberId, mId, memberName, showMedical },
      });
    } else if (action === 'pay') {
      trackClaimsAdobeEvent('1212', `${showMedical === false ? 'Behavioral' : 'Medical'}_Claims_pay_your_bill_click`, 6);
      navigate(NavigateConst.paySentaraBillURL);
    }
  };

  if (loading || (!claimData && relatedClaims.length === 0)) return <Loader />;

  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4 not-print">
        <Button
          iconLeft={<ChatMinimizeIcon />}
          data-testid="Backbtn"
          variant="icon_btn"
          onClick={() => navigate(-1)}
        >
          {t('common.back')}
        </Button>
      </div>
      
      <Headings
        level={5}
        variant="medium"
        text={
          <Trans
            i18nKey="medicalClaims.relatedClaimsTitle"
            values={{ typeOfCare: formatMedicalText(claimDetails?.typeOfCare ?? '')}}
            components={{ span: <span className='text-decoration-underline text-shaded-green' /> }}
            />
        }     
      />
       
      {/* web responsive header */}
      <div className="claim-details mt-4 print border d-md-none d-block p-3 mb-4 rounded-4">
        <div className="d-flex flex-column">
          <div className="d-flex mb-3">
            <Paragraph
              text={t('medicalClaims.serviceDate')}
              variant="bold"
              className="m-0 text-nowrap"
            />
            <Paragraph text={claimDetails?.serviceDate} className="m-0 ms-2" />
          </div>

          <div className="d-flex mb-3">
            <Paragraph
              text={t('medicalClaims.claimId')}
              variant="bold"
              className="m-0 text-nowrap"
            />
            <Paragraph text={claimId} className="m-0 ms-2" />
          </div>

          <div className="d-flex mb-3">
            <Paragraph
              text={t('medicalClaims.provider')}
              variant="bold"
              className="m-0 text-nowrap"
            />
            <Paragraph
              text={formatMedicalText(claimDetails?.providerName)}
              className="m-0 ms-2"
            />
          </div>

          <div className="d-flex">
            <Badge
              status={claimDetails?.status ?? ''}
              labelText={t('medicalClaims.badgeStatus')}
              className="rounded-pill"
            />
          </div>
        </div>
      </div>

      {/* web header */}
      <div className="claim-details mt-4 print d-md-block d-none">
        <div className="row mb-2">
          <div className="col-12 col-md-6">
            <div className="d-flex">
              <Paragraph
                text={t('medicalClaims.serviceRelatedClaimsDate')}
                variant="bold"
                className="m-0 text-nowrap"
              />
              <Paragraph
                text={claimDetails?.serviceDate}
                className="m-0 ms-2"
              />
            </div>

            <div className="d-flex mt-2">
              <Paragraph
                text={t('medicalClaims.claimId')}
                variant="bold"
                className="m-0 text-nowrap"
              />
              <Paragraph text={claimId} className="m-0 ms-2" />
            </div>
          </div>

          <div className="col-12 col-md-5 offset-md-1">
            <div className="d-flex offset-md-3">
              <Paragraph
                text={t('medicalClaims.provider')}
                variant="bold"
                className="m-0"
              />
              <Paragraph
                text={formatMedicalText(claimDetails?.providerName)}
                className="m-0 ms-2"
              />
            </div>

            <div className="d-flex mt-2 offset-md-3">
              <Paragraph
                text={t('medicalClaims.status')}
                variant="bold"
                className="m-0"
              />
              <Paragraph text={claimDetails?.status} className="m-0 ms-2" />
            </div>
          </div>
        </div>
      </div>

      {relatedClaims.length > 0 && (
        <div className="mt-4 mb-4 not-print">
          <div className="d-flex align-items-center gap-2 justify-content-md-end">
            <div className="d-flex align-items-center flex-nowrap flex-grow-1 flex-md-grow-0 py-1">
              <Paragraph
                text={t('medicalClaims.sortBy')}
                variant="regular"
                className="me-2 m-0 p-0 text-nowrap"
              />
              <Select
                id="related-claims-sort-select"
                className="form-select form-select-sm flex-grow-1 flex-md-grow-0"
                dataTestId="related-claims-sort-select"
                value={selectedSortField}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setSelectedSortField(e.target.value as SortField | '')
                }
              >
                <option value="" disabled hidden>
                  {t('memberIdCard.select')}
                </option>
                {claimsSortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="py-1">
              <Button
                variant={isMobile ? 'icon_btn' : 'primary_btn'}
                iconLeft={
                  showDesktopFilterIcon ? <FilterIconWhite fill="white" /> : null
                }
                onClick={() => setShowFilter(true)}
              >
                {isMobile
                  ? showMobileFilterIcon && (
                      <FilterIconWhiteWithBg
                        fill="white"
                        background="secondary"
                      />
                    )
                  : t('medicalClaims.filterTitle')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ClaimsFilter
        visible={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onCancel={() => setShowFilter(false)}
        onClearAll={handleClearAll}
        claims={relatedClaims}
      />

      {queriedClaims.length === 0 && !loading && (
        <div
          className="mb-4 text-center no-results"
          data-testid="noRelatedClaims"
        >
          {t('medicalClaims.noRelatedClaims')}
        </div>
      )}

      {queriedClaims.length > 0 && (
        <div className="print mt-4">
          <ClaimsAccordionWrapper
            claims={displayedClaims}
            onClick={handleClick}
          />
          {hasMoreItemsToLoad && <div ref={intersectionObserverTargetRef} />}
        </div>
      )}

      {isAvMed() ? null : (
        <div className="black-color pt-4">
          <Paragraph
            text={t('medicalClaims.disclaimer1')}
            variant="regular"
            className="discList"
            data-testid="disclaimerHeader"
          />
          {apiResponseSafeCheck(phone?.data) ? (
            <Paragraph
              text={
                <>
                  {t('medicalClaims.disclaimer21')}{' '}
                  <ContactInfo
                    data-testid="contactInfo"
                    phoneData={{
                      phone1800: phone?.data?.benefitPhone1800,
                      phoneLocal: phone?.data?.benefitPhoneLocal,
                    }}
                  />
                  {t('medicalClaims.disclaimer22')}
                </>
              }
              variant="regular"
              className="discList"
              data-testid="disclaimerHeader"
            />
          ) : (
            <Paragraph
              text={t('medicalClaims.disclaimer23')}
              variant="regular"
              className="discList"
              data-testid="disclaimerHeader"
            />
          )}
          <Paragraph
            text={t('medicalClaims.disclaimer3')}
            variant="regular"
            className="discList "
            data-testid="disclaimerHeader"
          />
          <Paragraph text={t('medicalClaims.disclaimer4')} variant="regular" />
          <Paragraph
            text={
              <>
                {t('medicalClaims.disclaimer5')}
                <Link
                  to={`mailto:${t('medicalClaims.disclaimer6')}`}
                  className="ws-nowrap text-decoration-underline px-1"
                  data-testid="customerMailId"
                >
                  {t('medicalClaims.disclaimer6')}
                </Link>
                {apiResponseSafeCheck(phone?.data) ? (
                  <>
                    {t('medicalClaims.disclaimer7')}{' '}
                    <ContactInfo
                      data-testid="contactInfo"
                      phoneData={{
                        phone1800: phone?.data?.benefitPhone1800,
                        phoneLocal: phone?.data?.benefitPhoneLocal,
                      }}
                      className="ps-1"
                    />
                  </>
                ) : null}
              </>
            }
            variant="regular"
            data-testid="disclaimer5"
            className="mb-5"
          />
        </div>
      )}
    </div>
  );
};

export default SuperRelatedClaims;
